import { useState } from "react"
import { FolderTree } from "lucide-react"
import {
  useGetActiveBudgetCategoriesQuery,
  useCreateBudgetCategoryMutation,
  useUpdateBudgetCategoryMutation,
} from "shared/redux/endpoints/procurementV2"
import { Button } from "../../ui"
import { DataTable, StatusPill, EmptyState, en } from "procurement/design"
import CategoryFormModal from "./CategoryFormModal"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"

// Category admin — the only FE surface for pro.budget_category's `code`,
// SUN accounts and the 2 barrier-removal flags (mustHaveOrder/
// mustHavePurchase, 2026-09 feature). Categories used to only be created
// inline from AddBudgetLineModal.js (Budget page); this is the first real
// edit UI, added here (Item Management) rather than a new top-level page
// since Finance/Procurement/PM already land on this page for item admin.
const CategoriesListView = () => {
  const { data: categories, isFetching } = useGetActiveBudgetCategoriesQuery({})
  const [createCategory, { isLoading: creating }] = useCreateBudgetCategoryMutation()
  const [updateCategory, { isLoading: updating }] = useUpdateBudgetCategoryMutation()
  const busy = creating || updating

  const [modalOpen, setModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)

  const openCreate = () => {
    setEditingCategory(null)
    setModalOpen(true)
  }
  const openEdit = (row) => {
    setEditingCategory(row)
    setModalOpen(true)
  }
  const closeModal = () => !busy && setModalOpen(false)

  const handleSave = async (payload) => {
    try {
      if (editingCategory) {
        await updateCategory({ id: editingCategory.id, ...payload }).unwrap()
        showToastSuccess(en.toast.categoryUpdated)
      } else {
        await createCategory(payload).unwrap()
        showToastSuccess(en.toast.categoryCreated)
      }
      setModalOpen(false)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't save this category. Please try again."))
    }
  }

  const toggleFlag = async (row, key, next) => {
    try {
      await updateCategory({
        id: row.id,
        code: row.code,
        name: row.name,
        assetClassCode: row.assetClassCode,
        sunPlAccount: row.sunPlAccount,
        sunBsAccount: row.sunBsAccount,
        isActive: row.isActive,
        mustHaveOrder: row.mustHaveOrder,
        mustHavePurchase: row.mustHavePurchase,
        [key]: next,
      }).unwrap()
      showToastSuccess(en.toast.categoryUpdated)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't update this category."))
    }
  }

  const columns = [
    {
      key: "code",
      header: en.categories.code,
      sortable: true,
      render: (r) => (
        <span style={{ fontVariantNumeric: "tabular-nums", color: "var(--text-secondary)" }}>
          {r.code || "—"}
        </span>
      ),
    },
    {
      key: "name",
      header: en.categories.name,
      strong: true,
      sortable: true,
      render: (r) => <span className="ds-title-2">{r.name}</span>,
    },
    { key: "assetClassCode", header: en.categories.assetClassCode, render: (r) => r.assetClassCode || "—" },
    {
      key: "isActive",
      header: en.categories.active,
      render: (r) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }} onClick={(e) => e.stopPropagation()}>
          <StatusPill status={r.isActive ? "active" : "inactive"} />
          <input
            type="checkbox"
            checked={!!r.isActive}
            onChange={(e) => toggleFlag(r, "isActive", e.target.checked)}
            aria-label={r.isActive ? "Set inactive" : "Set active"}
          />
        </span>
      ),
    },
    {
      key: "mustHaveOrder",
      header: en.categories.allowPoWithoutOrder,
      render: (r) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }} onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={!r.mustHaveOrder}
            onChange={(e) => toggleFlag(r, "mustHaveOrder", !e.target.checked)}
            aria-label="Allow PO without Order"
          />
        </span>
      ),
    },
    {
      key: "mustHavePurchase",
      header: en.categories.allowPaymentWithoutPo,
      render: (r) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }} onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={!r.mustHavePurchase}
            onChange={(e) => toggleFlag(r, "mustHavePurchase", !e.target.checked)}
            aria-label="Allow Payment without PO"
          />
        </span>
      ),
    },
  ]

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Button variant="primary" onClick={openCreate}>
          {en.categories.newCategory}
        </Button>
      </div>
      <DataTable
        columns={columns}
        rows={categories || []}
        isLoading={isFetching}
        onRowClick={openEdit}
        initialSort={{ key: "name", dir: "asc" }}
        empty={
          <EmptyState
            icon={<FolderTree aria-hidden />}
            title={en.categories.emptyCategories}
            text={en.categories.emptyCategoriesText}
            action={
              <Button variant="primary" onClick={openCreate}>
                {en.categories.newCategory}
              </Button>
            }
          />
        }
      />
      <CategoryFormModal
        open={modalOpen}
        onClose={closeModal}
        category={editingCategory}
        busy={busy}
        onSave={handleSave}
      />
    </>
  )
}

export default CategoriesListView
